import type { FirebaseUser } from '@creator/sdk/modules/account/account.model';
import creatorSdk from '@src/services/creator-sdk';
import storageService from '@src/services/storage-service';
import store from '@src/store';
import { saveAuthToLocalStorage } from '../user/actions';
import { getUserIdByUsername } from '../user/helpers';
import { changeAccountPasswordApiCall, createAccountApiCall, firebaseSignInApiCall } from './account';
import { generateKeyPair, generateKeyPairDeprecated } from './blockchain-auth';

export interface CreateAccountPayload {
    username: string;
    password?: string;
    firebaseUser: FirebaseUser;
}

export interface ChangePasswordPayload {
    userId: string;
    password: string;
    firebaseUser: FirebaseUser;
}

export interface LoginPayload {
    username: string;
    password: string;
    domainName: string;
}

export interface loginWithoutPasswordPayload {
    username: string;
    firebaseUser: FirebaseUser;
    domainName: string;
}

export async function createAccount(payload: CreateAccountPayload) {
    const { username, password, firebaseUser } = payload;

    const referredBy = storageService.localStorage.get('referredBy') || '';
    const referredTokenName = storageService.localStorage.get('referredTokenName') || '';
    const refBy = storageService.localStorage.get('refBy') || '';
    const refFrom = storageService.localStorage.get('refFrom') || '';
    const receipt = await createAccountApiCall(username, password, referredBy, referredTokenName, refBy, refFrom);

    const { user, keyPair } = receipt;

    // Key pair is available only when password was given
    const publicKey = keyPair ? keyPair.publicKey : '';
    const privateKey = keyPair ? keyPair.privateKey : '';
    const displayName = user.displayName;

    saveAuthToLocalStorage(user.id, displayName, publicKey, privateKey);

    store.dispatch.user.authenticate({ user, firebaseUser });
    return { receipt, user, referredBy, refBy };
}

export async function login(payload: LoginPayload) {
    const { username, password, domainName } = payload;
    store.dispatch.user.setIsAuthenticating(true);

    const userId = getUserIdByUsername(username, domainName);
    const user = await creatorSdk.accountModule.getUser(userId).catch(error => {});
    if (!user) throw new Error('invalidUsernameOrPassword');

    const keyPair = user.passVer ? generateKeyPair(userId, password) : generateKeyPairDeprecated(username, password);

    const firebaseAuthResult = await firebaseSignInApiCall(userId, keyPair).catch(error => {
        store.dispatch.user.setIsAuthenticating(false);
        if (error.message?.includes('Login failed too many times')) throw new Error('loginFailedTooManyTimes');
        if (error.message?.includes('Invalid login')) throw new Error('invalidUsernameOrPassword');
        if (error.code === 'auth/user-not-found') throw new Error('invalidUsernameOrPassword');
        throw error;
    });

    if (!firebaseAuthResult.user) throw new Error('login failed invalid firebase user');
    const { privateKey, publicKey } = keyPair;

    saveAuthToLocalStorage(user.id, username, publicKey, privateKey);
    store.dispatch.user.authenticate({ user, firebaseUser: firebaseAuthResult.user });
    store.dispatch.user.setIsAuthenticating(false);
}

export async function changePassword(payload: ChangePasswordPayload) {
    const { password, userId } = payload;
    const { user, firebaseUser, keyPair } = await changeAccountPasswordApiCall(userId, password);
    const { privateKey, publicKey } = keyPair;
    saveAuthToLocalStorage(userId, user.username, publicKey, privateKey, user.providerEmail || '');
    store.dispatch.user.authenticate({ user, firebaseUser });
}

export async function loginWithoutPassword(payload: loginWithoutPasswordPayload) {
    const { username, firebaseUser, domainName } = payload;
    store.dispatch.user.setIsAuthenticating(true);

    const userId = getUserIdByUsername(username, domainName);
    const user = await creatorSdk.accountModule.getUser(userId);

    if (!user) {
        store.dispatch.user.setIsAuthenticating(false);
        throw new Error('invalidUsernameOrPassword');
    }

    store.dispatch.user.authenticate({ user, firebaseUser });
    saveAuthToLocalStorage(user.id, username, '', '');
    return user;
}
